import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useUser } from '../hooks/useUser.ts';
import { ApiCallError, apiFetch } from '../lib/api.ts';

interface LoginResponse {
  user: { id: number; email: string; name: string; role: string };
}

export function LoginPage() {
  const { user, isLoading } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);

  // If already authenticated, bounce back to where they came from (or home).
  if (!isLoading && user) {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from
      ?.pathname;
    return <Navigate to={from ?? '/'} replace />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch<LoginResponse>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password, remember }),
      });
      // Refresh useUser so ProtectedRoute lets us through.
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      const from = (location.state as { from?: { pathname?: string } } | null)?.from
        ?.pathname;
      navigate(from ?? '/', { replace: true });
    } catch (err) {
      if (err instanceof ApiCallError) setError(err.message);
      else setError('Unexpected error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        padding: '2rem',
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          width: 'min(380px, 100%)',
          padding: '2.5rem',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 4,
        }}
      >
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            margin: 0,
            color: 'var(--accent)',
            letterSpacing: '0.15em',
          }}
        >
          REDAWN
        </h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4, marginBottom: '2rem' }}>
          Sign in to continue
        </p>
        <label style={{ display: 'block', marginBottom: '1rem' }}>
          <span style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ width: '100%' }}
            autoComplete="username"
          />
        </label>
        <label style={{ display: 'block', marginBottom: '1rem' }}>
          <span style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Password</span>
          <span style={{ display: 'flex', gap: '0.4rem' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ flex: 1, minWidth: 0 }}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-pressed={showPassword}
              title={showPassword ? 'Hide password' : 'Show password'}
              style={{
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: 2,
                color: 'var(--text-muted)',
                cursor: 'pointer',
                fontSize: 12,
                padding: '0 0.6rem',
              }}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </span>
        </label>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1.5rem',
            fontSize: 13,
            color: 'var(--text-muted)',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            style={{ width: 'auto', margin: 0 }}
          />
          Keep me signed in on this device
        </label>
        {error && (
          <p style={{ color: 'var(--danger)', marginTop: 0, fontSize: 13 }}>{error}</p>
        )}
        <button
          type="submit"
          disabled={submitting}
          style={{
            width: '100%',
            padding: '0.7rem 1rem',
            background: 'var(--accent)',
            color: 'var(--bg)',
            border: 'none',
            borderRadius: 2,
            fontSize: '13px',
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.6 : 1,
          }}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
